<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="pt-0">
          <v-container fluid pt-0>
            <v-row align="center">
              <div>
                <h1 class="headline" v-if="showTitle">Histories</h1>
              </div>
              <v-col class="py-0">
                <list-filters
                  :filters="historiesFilters"
                  label="Search"
                  :ref-name="refName"
                  :cached-filters="cachedFilters"
                  @searchParamsChanged="searchParamsChanged($event)"
                  :expansionPanel="false"
                ></list-filters>
              </v-col>
              <div>
                <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
              </div>
              <div>
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <ag-grid-vue
        v-if="showAgGrid"
        class="ag-theme-alpine"
        id="ag-hist-list"
        :columnDefs="agGridColumns"
        :rowData="items"
        rowSelection="multiple"
        :context="agContext"
        @grid-ready="onAgGridReady"
        @columnMoved="onAgColumnMoved"
        @sortChanged="onAgSortChanged"
        @selection-changed="onAgSelectionChanged"
        @newColumnsLoaded="onAgNewColumns"
        suppressDragLeaveHidesColumns
        domLayout="autoHeight"
      >
      </ag-grid-vue>
      <list-actions
        v-if="showAgGrid && selected.length > 0"
        style="top: -30px; position: relative"
        :selected="selected"
        list="history"
        :columns="visibleColumns"
        :filter="searchParams"
      >
      </list-actions>
      <v-data-table
        v-if="!showAgGrid"
        :headers="filteredHeaders"
        :items="items"
        :loading="$apollo.loading"
        :search="search"
        item-key="id"
        class="elevation-1"
        show-group-by
        show-select
        v-model="selected"
        v-bind="pagingOptions"
        @update:options="onListOptionsUpdate"
        fixed-header
        hide-default-footer
        :height="gridHeight"
      >
        <template v-slot:item.totalCost="{ item }">
          {{ item.totalCost | priceLabeler }}
        </template>
        <template v-slot:item.totalSell="{ item }">
          {{ item.totalSell | priceLabeler }}
        </template>
        <template v-slot:item.totalCostInBase="{ item }">
          {{ item.totalCostInBase | priceLabeler }}
        </template>

        <template v-slot:item.clientName="{ item }">
          <client-label
            v-model="item.clientName"
            :client-id="item.itineraryClientId"
          ></client-label>
        </template>
        <template v-slot:item.itineraryReference="{ item }">
          <reference-label
            v-model="item.itineraryReference"
            :itinerary-id="item.itineraryId"
          ></reference-label>
        </template>
        <template v-slot:item.itineraryAgentName="{ item }">
          <agent-label
            v-model="item.itineraryAgentName"
            :agent-id="item.itineraryAgentId"
          ></agent-label>
        </template>
        <template v-slot:item.supplierName="{ item }">
          <supplier-label
            v-model="item.supplierName"
            :supplier-type="item.serviceTypeName"
            :supplier-id="item.supplierId"
          ></supplier-label>
        </template>
        <template v-slot:item.status="{ item }">
          <status-label v-model="item.status"></status-label>
        </template>
        <template v-slot:item.itineraryStatus="{ item }">
          <status-label v-model="item.itineraryStatus"></status-label>
        </template>
        <template v-slot:item.date="{ item }">{{
          item.date | formatDate
        }}</template>
        <template v-slot:item.itineraryStartDate="{ item }">{{
          item.itineraryStartDate | formatDate
        }}</template>
        <template v-slot:item.itineraryEndDate="{ item }">{{
          item.itineraryEndDate | formatDate
        }}</template>
        <template v-slot:item.isCustomPrice="{ item }">
          <v-layout justify-center>
            <v-checkbox
              v-model="item.isCustomPrice"
              hide-details
              :disabled="true"
              class="pa-0 ma-0"
            ></v-checkbox>
          </v-layout>
        </template>
        <template v-slot:item.allocationUID="{ item }">
          <v-layout justify-center>
            <v-checkbox
              v-model="item.allocationUID"
              hide-details
              :disabled="true"
              class="pa-0 ma-0"
            ></v-checkbox>
          </v-layout>
        </template>
        <template v-slot:item.itineraryNotes="{ item }">
          <div v-html="item.itineraryNotes"></div>
        </template>
        <template v-slot:item.isHistoricalRate="{ item }">
          <v-layout justify-center>
            <v-checkbox
              v-model="item.isHistoricalRate"
              hide-details
              :disabled="true"
              class="pa-0 ma-0"
            ></v-checkbox>
          </v-layout>
        </template>
        <template v-slot:footer>
          <list-actions
            v-if="selected.length > 0"
            :selected="selected"
            list="history"
            :columns="visibleColumns"
            :filter="listActionFilters"
          >
          </list-actions>
        </template>
      </v-data-table>

      <get-more-results
        :first="first"
        @limitChange="onLimitChange"
        :items="itineraryHistories"
        :totalItems="totalItems"
        :loading="$apollo.loading"
        :loadMoreEnabled="loadMoreEnabled"
        @loadMore="loadMore('itineraryHistories')"
      ></get-more-results>
      <v-row v-if="debug">
        <v-col>
          <pre>{{ historiesFilters }}</pre>
        </v-col>
      </v-row>
    </v-flex>
    <v-dialog v-model="$apollo.loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Loading Data...
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import GetMoreResults from "../../shared/GetMoreResults";
import ListFilters from "../../shared/list-filters/ListFilters";
import { ALL_HISTORIES_SEARCH_QUERY } from "../../../../constants/graphql";
import filters from "./filters";
import { sortByDate, sortByReference } from "../../../../helpers/index";
import StatusLabel from "../../shared/labels/StatusLabel";
import SupplierLabel from "../../shared/labels/SupplierLabel";
import AgentLabel from "../../shared/labels/AgentLabel";
import ReferenceLabel from "../../shared/labels/ReferenceLabel";
import ClientLabel from "../../shared/labels/ClientLabel";
import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../../shared/AgGridSwitch";
import AgGridListMixin from "../../../../mixins/AgGridListMixin";
import ListActions from "../../shared/list-actions/ListActions";
export default {
  name: "history-list",

  props: {
    customFilters: {
      type: Object,
    },
    refName: {
      type: String,
      default: () => "trackerFilters",
    },
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    cachedFilters: {
      type: Boolean,
      default: () => false,
    },
    refNameColumns: {
      type: String,
      default: () => "history-list-cols",
    },
    hideFilters: {
      type: Array,
      default: () => [],
    },
    hideColumns: {
      type: Array,
      default: () => [],
    },
  },

  mixins: [ListMixin, AgGridListMixin],

  components: {
    ListVisibleColumnsAndFilter,
    GetMoreResults,
    ListFilters,
    StatusLabel,
    SupplierLabel,
    AgentLabel,
    ReferenceLabel,
    ClientLabel,
    AgGridSwitch,
    AgGridVue,
    ListActions,
  },
  data() {
    return {
      debug: false,
      filters,
      itineraryHistories: {},
      selected: [],
      search: "",
      searchParams: {},
      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "itineraryReference",
        "date",
        "serviceName",
        "supplierName",
        "actions",
      ]),
      headers: [
        {
          text: "ID",
          value: "groupId",
          name: "groupId",
        },
        {
          text: "Iti Ref",
          value: "itineraryReference",
          name: "itineraryReference",
          sort: sortByReference,
          comparator: sortByReference,
        },

        {
          text: "Iti Internal Ref",
          value: "itineraryInternalReference",
          name: "itineraryInternalReference",
        },
        {
          text: "Iti Internal Status",
          value: "itineraryInternalStatus",
          name: "itineraryInternalStatus",
        },
        {
          text: "Iti Department",
          value: "itineraryDepartment",
          name: "itineraryDepartment",
        },
        {
          text: this.$store.getters.isCPS ? "Itinerary Destination CPS" : "Iti Brand",
          value: "itineraryBrand",
          name: "itineraryBrand",
        },
        {
          text: this.$store.getters.isCPS ? "Operations Team" : "Lead Source",
          value: "itineraryLeadSource",
          name: "itineraryLeadSource",
        },
        {
          text: "Iti Start Date",
          value: "itineraryStartDate",
          name: "itineraryStartDate",
        },
        {
          text: "Iti End Date",
          value: "itineraryEndDate",
          name: "itineraryEndDate",
        },
        {
          text: "Supplier Head Office",
          value: "supplierHeadOffice",
          name: "supplierHeadOffice",
        },
        {
          text: "Supplier Region",
          value: "supplierRegion",
          name: "supplierRegion",
        },
        {
          text: "Sales Nominal Code",
          value: "supplierNominalCode",
          name: "supplierNominalCode",
        },
        {
          text: "Purchase Nominal Code",
          value: "supplierPurchaseNominalCode",
          name: "supplierPurchaseNominalCode",
        },
        {
          text: "Sales Invoices",
          value: "salesInvoices",
          name: "salesInvoices",
          sortable: false,
        },
        {
          text: "PI Numbers",
          value: "purchaseInvoices",
          name: "purchaseInvoices",
          sortable: false,
        },
        {
          text: "Historical",
          value: "isHistoricalRate",
          name: "isHistoricalRate",
          sort: false,
        },
        /*
        {
          text: "Optional",
          value: "isOptional",
          name: "isOptional",
          sort: false,
        },
        {
          text: "Included",
          value: "isIncluded",
          name: "isIncluded",
          sort: false,
        },
        */
        {
          text: "Start Time",
          value: "pickupTime",
          name: "pickupTime",
          sort: false,
        },
        {
          text: "End Time",
          value: "dropoffTime",
          name: "dropoffTime",
          sort: false,
        },
        {
          text: "Voucher Notes",
          value: "voucherNotes",
          name: "voucherNotes",
          sort: false,
        },
        {
          text: "Confirmation No",
          value: "confirmationNo",
          name: "confirmationNo",
          sort: false,
        },
        {
          text: "Date",
          value: "date",
          name: "date",
          sort: sortByDate,
        },

        {
          text: "Supplier",
          value: "supplierName",
          name: "supplierName",
        },
        {
          text: "Service",
          value: "serviceName",
          name: "serviceName",
        },
        {
          text: "Adults",
          value: "adults",
          name: "adults",
        },
        {
          text: "Children",
          value: "children",
          name: "children",
        },
        {
          text: "Infants",
          value: "infants",
          name: "infants",
        },
        {
          text: "Qty",
          value: "qty",
          name: "qty",
        },
        {
          text: "Status",
          value: "status",
          name: "status",
        },

        {
          text: "Notes",
          value: "notes",
          name: "notes",
        },
        {
          text: "Total Cost",
          value: "totalCost",
          name: "totalCost",
        },
        {
          text: "Total Sell",
          value: "totalSell",
          name: "totalSell",
        },
        {
          text: "Custom Price",
          value: "isCustomPrice",
          align: "center",
          name: "isCustomPrice",
        },
        {
          text: "Currency Sell",
          value: "sellCurrencyCode",
          name: "sellCurrencyCode",
        },
        {
          text: "Service Currency",
          value: "buyCurrencyCode",
          name: "buyCurrencyCode",
        },
        {
          text: "Rate Type Code",
          value: "rateTypeCode",
          name: "rateTypeCode",
        },
        {
          text: "Rate Type Name",
          value: "rateTypeName",
          name: "rateTypeName",
        },
        {
          text: "Agent",
          value: "itineraryAgentName",
          name: "itineraryAgentName",
          sortable: false,
        },
        {
          text: "Client",
          value: "clientName",
          name: "clientName",
          sortable: false,
        },
        {
          text: "Supplier Destination",
          value: "supplierDestination",
          name: "supplierDestination",
          sortable: false,
        },
        {
          text: "Itinerary Destination",
          value: "itineraryDestination",
          name: "itineraryDestination",
          sortable: false,
        },
        {
          text: "Itinerary Notes",
          value: "itineraryNotes",
          name: "itineraryNotes",
          sortable: false,
        },
        {
          text: "Booked By",
          value: "itineraryBookedBy",
          name: "itineraryBookedBy",
          sortable: false,
        },
        {
          text: "Created By",
          value: "itineraryCreatedBy",
          name: "itineraryCreatedBy",
          sortable: false,
        },
        {
          text: "Iti Creation Date",
          value: "itineraryCreatedAt",
          name: "itineraryCreatedAt",
          sortable: false,
        },
        {
          text: "Basis",
          value: "basis",
          name: "basis",
          sortable: false,
        },
        {
          text: "Total Cost In Base",
          value: "totalCostInBase",
          name: "totalCostInBase",
          sortable: false,
        },
        {
          text: "From Allocation",
          value: "allocationUID",
          name: "allocationUID",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    selectableHeaders() {
      return this.headers.filter((h) => {
        if (h.name == "isHistoricalRate") {
          return this.$store.getters.hasAccessTo("itineraries:look_back", true);
        } else {
          return (
            h.name != "menuActions" && this.hideColumns.indexOf(h.name) == -1
          );
        }
      });
    },
    filteredHeaders() {
      // return [
      //   ...this.headers.filter(
      //     (h) =>
      //       this.visibleColumns.indexOf(h.name) > -1 ||
      //       h.name == "actions" ||
      //       h.name == "menuActions"
      //   ),
      // ];
      const hs = [];
      // for (const headerName of this.visibleColumns.filter(
      //   (c) => c != "actions"
      // )) {

      /*
        http://tracker.globetrack.ie/issues/5658
        this way you are able to controll columns order by simply manipulating header table...

      const hs = this.headers.filter((h) =>
        this.visibleColumns.includes(h.name)
      );
      */

      for (const headerName of this.visibleColumns) {
        const h = this.headers.find((h) => h.name == headerName);
        if (h && this.hideColumns.indexOf(h.name) == -1) {
          hs.push(h);
        }
      }

      // const actionsHeader = this.headers.find((h) => h.name == "actions");
      // if (actionsHeader) {
      //   hs.push(actionsHeader);
      // }
      return this.hideActions ? hs.filter((h) => h.name != "actions") : hs;
    },

    listActionFilters() {
      return { ...this.searchParams, ...this.customFilters };
    },
    items() {
      return this.itineraryHistories && this.itineraryHistories.edges
        ? this.itineraryHistories.edges.map(this.mapHistories)
        : [];
    },
    historiesFilters() {
      return filters
        .map((f) => {
          f.props.outlined =
            this.$store.getters.localSettings("outlinedInputs");
          if (this.$store.getters.isCPS) {
            if (f.props.filterName == "leadSources_eq") {
              f.props.label = "Operations Team";
            }
            if (f.props.filterName == "brands_eq") {
              f.props.label = "Itinerary Destination";
            }
          }
          if (f.props.filterName == "date_from") {
            return {
              ...f,
              defaultLabel: `${this.$options.filters.formatDate(
                new Date().toISOString().split("T")[0]
              )} - ${this.$options.filters.formatDate(
                new Date().toISOString().split("T")[0]
              )}`,
            };
          } else {
            return f;
          }
        })
        .filter((f) => this.hideFilters.indexOf(f.props.filterName) == -1);
    },
  },
  methods: {
    mapHistories(item) {
      const { supplier, itinerary, ...history } = item.node;
      const itiData = {};
      if (itinerary) {
        itiData.itineraryId = itinerary.id;
        itiData.itineraryClientId = itinerary.client ? itinerary.client.id : "";
        itiData.itineraryAgentName = itinerary.agentName;
        itiData.itineraryAgentId = itinerary.agentId;
        itiData.itineraryInternalStatus = itinerary.internalStatus;
        itiData.itineraryInternalReference = itinerary.internalReference;
        itiData.itineraryStartDate = itinerary.startDate;
        itiData.itineraryEndDate = itinerary.endDate;
        itiData.itineraryDepartment = itinerary.department;
        itiData.supplierHeadOffice =
          supplier && supplier.headOffice ? supplier.headOffice.name : "";
        itiData.itineraryStatus = itinerary.bookingStatus;
        itiData.clientName = itinerary.clientName;
        itiData.itineraryDestination = itinerary.destinationName;
        itiData.itineraryBookedBy = itinerary.bookedByName;
        itiData.itineraryNotes = itinerary.notes;
        itiData.itineraryCreatedBy = itinerary.createdByName;
        itiData.itineraryCreatedAt = itinerary.createdAt;
        itiData.itineraryBrand = itinerary.brand;
        itiData.itineraryLeadSource = itinerary.leadSource;
      }
      const supplierData = {};
      if (supplier) {
        supplierData.supplierDestination = supplier.destinationName;
        supplierData.supplierRegion = supplier.region || "";
        supplierData.supplierNominalCode = supplier.nominalCode || "";
        supplierData.supplierPurchaseNominalCode =
          supplier.purchaseNominalCode || "";
      }
      return {
        ...history,
        ...itiData,
        ...supplierData,
        totalCost: history.total,
      };
    },
  },
  apollo: {
    itineraryHistories: {
      query: ALL_HISTORIES_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: {
            ...this.searchParams,
            ...this.customFilters,
          },
          first: this.first,
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {
        this.totalItems = data.itineraryHistories.totalCount;
      },
    },
  },
};
</script>

<style>
/* #ag-hist-list {
  height: calc(100vh - 320px);
} */
</style>



// WEBPACK FOOTER //
// src/components/gt/plugins/histories/HistoryList.vue